import React from 'react';
import { Box, Typography, Stack, MenuItem, Alert } from '@mui/material';
import { 
  Notes as AdditionalIcon,
  Message as CommIcon,
  Rule as RuleIcon,
  Info as InfoIcon,
  MarkunreadMailbox as MailboxIcon
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import FormCheckboxGroup from '../../shared/FormCheckboxGroup';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface AdditionalTermsStepProps {
  stepProgress: number;
}

const AdditionalTermsStep: React.FC<AdditionalTermsStepProps> = ({ stepProgress }) => {
  const { control } = useFormContext();

  const communicationOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'text', label: 'Text Message' },
    { value: 'mail', label: 'Regular Mail' },
  ];

  const maintenanceCommunicationOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'text', label: 'Text Message' },
    { value: 'portal', label: 'Online Portal' },
    { value: 'mail', label: 'Mail' },
  ];

  return (
    <StepFormLayout
      title="Additional Terms & Communication"
      description="Add any additional terms, rules, or special provisions"
      icon={<AdditionalIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={stepProgress}
      showOptionalBadge={true}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>📝 Tip:</strong> Clear communication preferences and additional terms help prevent misunderstandings later.
        </Typography>
      </Alert>
      
      {/* Communication Preferences */}
      <FormSection
        title="Communication Preferences"
        icon={<CommIcon color="primary" />}
        tooltip="How you prefer to communicate with tenants"
      >
        <Stack spacing={3}>
          <FormSelect
            name="additionalTerms.communicationPreference"
            control={control}
            label="Preferred Communication Method"
            required
          >
            {communicationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FormSelect>

          <FormTextField
            name="additionalTerms.maintenanceRequestMethod"
            control={control}
            label="Maintenance Request Method"
            required
            helperText="How should tenants submit maintenance requests?"
          />

          <FormCheckboxGroup
            name="additionalTerms.maintenanceCommunicationMethods"
            control={control}
            options={maintenanceCommunicationOptions}
            label="Accepted Maintenance Communication Channels"
          />
        </Stack>
      </FormSection>

      <FormSection
        title="Notice / Mailing Address"
        icon={<MailboxIcon color="primary" />}
        tooltip="Where official notices to the tenant should be sent"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave blank to use the premises address. Use this if the tenant wants notices mailed elsewhere.
        </Typography>
        <Stack spacing={2}>
          <FormTextField
            name="tenantNoticeAddress.street"
            control={control}
            label="Street Address"
            placeholder="123 Mailing St"
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
            <FormTextField
              name="tenantNoticeAddress.city"
              control={control}
              label="City"
            />
            <FormTextField
              name="tenantNoticeAddress.state"
              control={control}
              label="State"
              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            />
            <FormTextField
              name="tenantNoticeAddress.zip"
              control={control}
              label="ZIP"
              inputProps={{ maxLength: 10 }}
            />
          </Box>
        </Stack>
      </FormSection>

      {/* Additional Rules & Terms */}
      <FormSection
        title="Additional Rules & Terms"
        icon={<RuleIcon color="primary" />}
        tooltip="Specific rules, responsibilities, and special provisions"
      >
        <Stack spacing={3}>
          <FormTextField
            name="additionalTerms.rules"
            control={control}
            label="Property Rules"
            multiline
            rows={4}
            helperText="Any specific rules for the property (noise, common areas, etc.)"
          />

          <FormTextField
            name="additionalTerms.maintenanceResponsibilities"
            control={control}
            label="Maintenance Responsibilities"
            multiline
            rows={4}
            helperText="Specific maintenance responsibilities for tenant vs landlord"
          />

          <FormTextField
            name="additionalTerms.emergencyProcedures"
            control={control}
            label="Emergency Procedures"
            multiline
            rows={3}
            helperText="Emergency contact information and procedures"
          />

          <FormTextField
            name="additionalTerms.moveOutRequirements"
            control={control}
            label="Move-Out Requirements"
            multiline
            rows={4}
            helperText="Specific requirements for when tenant moves out"
          />

          <FormTextField
            name="additionalTerms.specialProvisions"
            control={control}
            label="Special Provisions"
            multiline
            rows={4}
            helperText="Any other special terms or provisions for this lease"
          />
        </Stack>
      </FormSection>
      

      </Box>
    </StepFormLayout>
  );
};

export default AdditionalTermsStep; 
